import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useAboutContent from "src/hooks/use-about-content";
import useReveal from "src/hooks/use-reveal";
import SectionBackdrop from "src/sections/components/section-backdrop";
import { SECTION_IMAGES } from "src/config/images";

// БЛОК «О НАС» НА ГЛАВНОЙ: кто мы и чем сильны.
//
// Содержимое правится в админке (настройка `about`), а чего там нет — берётся
// из переводов (см. src/hooks/use-about-content.js). Поэтому блок есть на
// странице всегда: раньше при пустой админке от «о нас» на главной оставалась
// одна ссылка «Подробнее», то есть кнопка без самого рассказа.
//
// Цифры компании сюда не входят намеренно: они стоят отдельной полосой выше,
// между первым экраном и «Ближайшими турами» (см. stats-band.jsx). Показывать
// одни и те же четыре числа дважды на одной странице незачем.
//
// Рамки у блока нет: фотография идёт во всю ширину окна, а текст лежит прямо
// на ней — как на первом экране. Скруглённая карточка поверх фотографии
// превращала кадр в картинку-плашку внутри страницы.
export default function About() {
  const { t } = useTranslation();
  const { title, text, advantages } = useAboutContent();
  const reveal = useReveal();

  // Абзацы разделяются пустой строкой — так текст приходит из админки.
  const paragraphs = text.value.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  return (
    <section
      id="about"
      ref={reveal.ref}
      className={`relative w-full py-10 sm:py-16 ${reveal.className}`}
    >
      {/* Фото во всю ширину окна — выбирается в src/config/images.js */}
      <SectionBackdrop image={SECTION_IMAGES.about} />
      <div className="relative max-w-[1526px] mx-auto px-4">
        <div className="grid gap-10 lg:gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div>
            <p className="on-photo text-xs sm:text-sm font-extrabold uppercase tracking-[0.28em] text-kj-gold">
              Kazakh Japan Tourism
            </p>
            <h2
              {...title.attrs}
              className="on-photo mt-3 text-2xl sm:text-4xl font-extrabold uppercase tracking-tight text-kj-ink text-balance"
            >
              {title.value}
            </h2>
            {paragraphs.map((paragraph, i) => (
              <p
                key={i}
                {...text.attrs}
                className="on-photo mt-5 text-base sm:text-lg text-kj-sub leading-relaxed max-w-[62ch]"
              >
                {paragraph}
              </p>
            ))}

            {/* Блок на главной — короткая версия. Кто хочет подробностей
                (история, синергия двух стран, все преимущества) — идёт на
                отдельную страницу; раньше выхода на неё отсюда не было. */}
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-[0.1em] text-kj-sky transition-colors hover:text-kj-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold rounded-sm"
            >
              {t("about.more")} →
            </Link>
          </div>

          {advantages.length > 0 && (
            <ul className="flex flex-col gap-6 lg:gap-7">
              {advantages.map((item, i) => (
                <li key={i} className="border-l-2 border-kj-gold/70 pl-4 sm:pl-5">
                  <h3
                    {...item.title.attrs}
                    className="on-photo text-base sm:text-lg font-extrabold uppercase tracking-tight text-kj-ink"
                  >
                    {item.title.value}
                  </h3>
                  <p
                    {...item.text.attrs}
                    className="on-photo mt-1.5 text-sm sm:text-base text-kj-sub leading-relaxed"
                  >
                    {item.text.value}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
